// The recruiter assignment on the requisition (G §16 "Assign/reassign Recruiter: Recruiter Request · Coordinator View ·
// HRBP Request · TA Mgr Approve"; G §5 "Recruiter assignment: Recommended -> Pending TA Manager -> Assigned."). Everyone
// on the record sees where the assignment stands and who holds it — never "Unassigned" (B1 D12). The assigned Recruiter
// and the requisition's HR Business Partner may ask for a reassignment with a reason ("Recruiter/HRBP may request
// reassignment; TA Manager approves.", `recruiter.request_reassignment`); the TA Manager decides it on the assignment
// queue, which this card links to while a Recruiter is still to be assigned. No Claim exists on any seat.
// The workload and expertise bars belong to the queue (the recommendation is the TA Manager's read).

import { useMemo, useState } from "react";
import { inputLengthHint, inputMaxLength } from "@/api/inputLimits";
import { recruiterAssign, recruiterRecommendation, recruiterRequestReassignment, type RequisitionFull } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Button, COMMON_LABELS, Card, DIALOG_LABELS, Dialog, Field, Flex, G1_LABELS, G6_CAPABILITY_LABELS, G6_RULE_LABELS, Paragraph, Person,
  PersonPick, Pill, ROLE_LABELS, Recorded, Stack, TextArea, TextLink,
} from "@/mc";
import { recruiterOptions } from "@/views/assignment/AssignmentQueue";
import { useSettledRead } from "@/views/shared/settledRead";
import { ASSIGNMENT_WORD } from "./data";
import { requisitionReads } from "./data";
import { ASSIGN_LABELS } from "./labels";

export function AssignmentCard({ r, mayRequest, mayAssign, mayReassign = false, open, onOpen, onClose, reassignOpen = false, onReassign, onReassignClose }: {
  r: RequisitionFull; mayRequest: boolean; mayAssign: boolean; open: boolean; onOpen: () => void; onClose: () => void;
  /** F-149 (DC-233): the seat holds `recruiter.assign` (TA Manager, Admin) — *Reassign*, done at once. */
  mayReassign?: boolean; reassignOpen?: boolean; onReassign?: () => void; onReassignClose?: () => void;
}) {
  const word = ASSIGNMENT_WORD[r.assignment_status];
  const ask = useWrite((input: { requisition_id: string; reason: string }, key) => recruiterRequestReassignment(input, key), requisitionReads(r.id));
  const [reason, setReason] = useState("");
  const fields = ask.error?.fieldErrors ?? {};
  const submit = () => void ask.mutateAsync({ requisition_id: r.id, reason: reason.trim() }).then(() => { setReason(""); onClose(); }).catch(() => undefined);
  return (
    <Card
      title={G6_CAPABILITY_LABELS.assignRecruiter}
      pills={word ? <Pill>{word}</Pill> : undefined}
      actions={(mayRequest || mayReassign) && r.recruiter_id ? (
        <Flex gap={8}>
          {mayReassign && onReassign ? <Button sm onClick={onReassign} >{ACTION_LABELS.reassign}</Button> : null}
          {mayRequest ? <Button sm tone="ghost" onClick={onOpen} >{ACTION_LABELS.requestReassignment}</Button> : null}
        </Flex>
      ) : undefined}
      
    >
      <Stack gap={12}>
        {r.recruiter_id && r.recruiter_name ? <Person name={r.recruiter_name} hueKey={r.recruiter_id} sub={word} /> : null}
        <Paragraph sm>{r.recruiter_id ? G6_RULE_LABELS.reassignmentApproval : G6_RULE_LABELS.taManagerAssigns}</Paragraph>
        {mayAssign && r.assignment_status !== "assigned" ? <TextLink href="#requisitions/assignment" >{ACTION_LABELS.assign}</TextLink> : null}
        {ask.isSuccess ? <Recorded live>{G6_RULE_LABELS.reassignmentApproval}</Recorded> : null}
      </Stack>
      {mayRequest ? (
        <Dialog
          title={ACTION_LABELS.requestReassignment}
          open={open}
          onClose={onClose}
          dismissLabel={DIALOG_LABELS.cancel}
          primary={{ label: ACTION_LABELS.requestReassignment, onClick: submit, busy: ask.isPending, hs: "assign.request" }}
          refused={ask.error && !fields.reason ? { reason: ask.error.remedy || ask.error.message } : undefined}
          
        >
          <Field label={G1_LABELS.reason} required error={fields.reason} hint={G1_LABELS.reasonStored}>
            <TextArea value={reason} onChange={(event) => setReason(event.target.value)}  />
          </Field>
        </Dialog>
      ) : null}
      {mayReassign && r.recruiter_id && reassignOpen && onReassignClose ? <ReassignDialog r={r} onClose={onReassignClose} /> : null}
    </Card>
  );
}

/**
 * F-149 (DC-233, C1): the TA Manager's and the Admin's direct reassignment — *Reassign Recruiter*: the current Recruiter,
 * the ranked list the queue uses (the recommended one first, the current Recruiter left out), a required reason, and
 * *Reassign* through the existing `recruiter.assign` (the requisition's row version): done at once, any pending request
 * decided by it. No empty Recruiter is ever sent: the primary is dashed until one is picked and a reason given.
 */
export function ReassignDialog({ r, onClose }: { r: RequisitionFull; onClose: () => void }) {
  const { manifest } = useSession();
  const options = useSettledRead(["recruiter-recommendation", r.id], () => recruiterRecommendation({ requisition_id: r.id }));
  const ranked = useMemo(() => (options.data?.ranked ?? []).filter((one) => one.person_id !== r.recruiter_id), [options.data, r.recruiter_id]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const reassign = useWrite(
    (input: { requisition_id: string; recruiter_id: string; expected_version: number; reason: string }, key) => recruiterAssign(input, key),
    [...requisitionReads(r.id), ["recruiter-queue"], ["recruiter-recommendation"]],
  );
  const limit = inputMaxLength(manifest, "recruiter.assign", "reason");
  const fields = reassign.error?.fieldErrors ?? {};
  const missing = !chosen ? ROLE_LABELS.recruiter : !reason.trim() ? G1_LABELS.reason : null;
  const submit = () => {
    if (!chosen || !reason.trim()) return;
    void reassign.mutateAsync({ requisition_id: r.id, recruiter_id: chosen, expected_version: r.row_version ?? 1, reason: reason.trim() })
      .then(onClose)
      .catch(() => undefined);
  };
  const recruiterError = fields.recruiter_id ? COMMON_LABELS.required : undefined;
  const reasonError = fields.reason ? COMMON_LABELS.required : undefined;
  return (
    <Dialog
      title={ASSIGN_LABELS.reassignRecruiter}
      open
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{
        label: ACTION_LABELS.reassign, onClick: submit, busy: reassign.isPending, hs: "assign.reassign",
        blocked: missing ? { reason: `${missing} · ${COMMON_LABELS.required}` } : undefined,
      }}
      refused={reassign.error && !recruiterError && !reasonError ? { reason: reassign.error.remedy || reassign.error.message } : undefined}
      
    >
      <Stack gap={12}>
        {r.recruiter_id ? <Person name={r.recruiter_name ?? r.recruiter_id} hueKey={r.recruiter_id} sub={ROLE_LABELS.recruiter} /> : null}
        <Field label={ROLE_LABELS.recruiter} required error={recruiterError}>
          <PersonPick
            loading={options.isPending}
            options={recruiterOptions(ranked, (one) => `assign.reassign.pick.${one.rank}`)}
            value={chosen ?? undefined}
            onChange={(value) => setChosen(value || null)}
            
          />
        </Field>
        <Field
          label={G1_LABELS.reason}
          required
          error={reasonError}
          hint={limit !== undefined && reason.length >= limit * 0.8 ? inputLengthHint(reason, limit) : G1_LABELS.reasonStored}
        >
          <TextArea value={reason} maxLength={limit} onChange={(event) => setReason(event.target.value)}  />
        </Field>
      </Stack>
    </Dialog>
  );
}
